import { useCallback, useEffect, useMemo, useState } from 'react'
import { formatCurrencyInput } from '@/lib/utils/formatters'
import {
  showApiError,
  showError,
  showSuccess,
} from '@/lib/utils/notifications'
import { openChargebackPacketDownload } from '@/features/application/services/applicationService'
import {
  addChargebackNote,
  deleteChargebackFile,
  generateChargebackPacket,
  getChargebackApplicationInfo,
  getChargebackNotes,
  getPaymentLogDetail,
  updateChargebackPaymentAdjustment,
  uploadChargebackFiles,
} from '../services/reportsService'
import type {
  ChargebackApplicationInfo,
  ChargebackNote,
  CoResidentApplicationEntry,
  PaymentLogDetailResponse,
} from '../types/api'
import type {
  ChargebackDetailChargebackTab,
  ChargebackDetailDecisionForm,
  ChargebackDetailInvestigationForm,
  ChargebackDetailUploadSelection,
} from '../types/pages'
import {
  EMPTY_DECISION_FORM,
  EMPTY_INVESTIGATION_FORM,
} from '../constants/chargebackDetailConstants'
import {
  amountForApi,
  isDecisionAllowed,
  normalizeBoolean,
  normalizeMulti,
  toYmd,
  unixSecondsToYmd,
} from '../utils/chargebackDetailFormatters'

export function useChargebackDetailPage(applicantId: string, logId: string) {
  const [activeTab, setActiveTab] = useState<ChargebackDetailChargebackTab>('investigation')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [fileBusy, setFileBusy] = useState(false)
  const [detail, setDetail] = useState<PaymentLogDetailResponse | null>(null)
  const [applicationInfo, setApplicationInfo] = useState<ChargebackApplicationInfo | null>(null)
  const [applicationData, setApplicationData] = useState<CoResidentApplicationEntry | null>(null)
  const [notes, setNotes] = useState<ChargebackNote[]>([])
  const [noteDraft, setNoteDraft] = useState('')
  const [investigation, setInvestigation] = useState<ChargebackDetailInvestigationForm>(
    EMPTY_INVESTIGATION_FORM,
  )
  const [decision, setDecision] = useState<ChargebackDetailDecisionForm>(EMPTY_DECISION_FORM)
  const [packetConfirmOpen, setPacketConfirmOpen] = useState(false)
  const [deleteFileId, setDeleteFileId] = useState<string | number | null>(null)

  const tabs = useMemo(
    () => [
      {
        id: 'investigation',
        label: 'Investigation',
        testId: 'chargeback-detail-tab-investigation',
      },
      {
        id: 'decision',
        label: 'Decision',
        testId: 'chargeback-detail-tab-decision',
      },
      {
        id: 'history',
        label: 'Chargeback History',
        testId: 'chargeback-detail-tab-history',
      },
    ],
    [],
  )

  const applyPaymentDefaults = useCallback(
    (
      base: ChargebackDetailInvestigationForm,
      appInfo: ChargebackApplicationInfo | null,
      row: PaymentLogDetailResponse,
    ) => {
      const next = { ...base }
      if (!appInfo || !row?.payment_log_id) return next
      if (!next.certificate_on_file) {
        next.certificate_on_file = normalizeBoolean(appInfo.rent_added) ? '1' : '0'
      }
      const paymentRows = Array.isArray(appInfo.payment_and_payment_adjustment)
        ? appInfo.payment_and_payment_adjustment
        : []
      const match = paymentRows.find(
        (payment) => String(payment?.payment_log_id) === String(row.payment_log_id),
      )
      if (match) {
        if (!next.card_merchant) next.card_merchant = String(match.payment_source ?? '')
        if (!next.card_no) next.card_no = String(match.card_no ?? '')
        if (!next.transaction_date) {
          next.transaction_date = unixSecondsToYmd(match.created_at)
        }
      }
      return next
    },
    [],
  )

  const loadNotes = useCallback(async () => {
    if (!logId) return
    const rows = await getChargebackNotes(logId)
    setNotes(rows)
  }, [logId])

  /** Refresh uploaded file lists without resetting form state or showing the page loader. */
  const refreshChargebackFiles = useCallback(async () => {
    if (!logId) return
    try {
      const detailData = await getPaymentLogDetail(logId, 'payment_adjustment')
      setDetail((prev) =>
        prev
          ? { ...prev, chargeback_files: detailData.chargeback_files }
          : detailData,
      )
    } catch (err) {
      showApiError(err)
    }
  }, [logId])

  const loadPage = useCallback(async () => {
    if (!applicantId || !logId) {
      setLoading(false)
      showError('Missing chargeback route information.')
      return
    }
    setLoading(true)
    try {
      const [detailData, appInfo, noteRows] = await Promise.all([
        getPaymentLogDetail(logId, 'payment_adjustment'),
        getChargebackApplicationInfo(applicantId),
        getChargebackNotes(logId),
      ])

      const normalizedDetail = {
        ...detailData,
        cancelled_application: normalizeBoolean(detailData?.cancelled_application),
        expire_application: normalizeBoolean(detailData?.expire_application),
        re_invoice: normalizeBoolean(detailData?.re_invoice),
        refunded: normalizeBoolean(detailData?.refunded),
        file_fraud: normalizeBoolean(detailData?.file_fraud),
        chargeback_notification_date:
          detailData?.chargeback_notification_date || detailData?.case_date || '',
      }

      const baseInvestigation: ChargebackDetailInvestigationForm = {
        chargeback_status: String(normalizedDetail.chargeback_status ?? ''),
        adjustment_reason: String(normalizedDetail.adjustment_reason ?? ''),
        case_number: String(normalizedDetail.case_number ?? ''),
        transaction_id: String(
          normalizedDetail.transaction_id ?? normalizedDetail.auth_code ?? '',
        ),
        chargeback_amount: formatCurrencyInput(
          normalizedDetail.adjustment_amount ?? normalizedDetail.chargeback_amount ?? '',
          true,
        ),
        response_deadline: toYmd(normalizedDetail.response_deadline),
        transaction_date: toYmd(normalizedDetail.transaction_date),
        chargeback_notification_date: toYmd(normalizedDetail.chargeback_notification_date),
        card_merchant: String(normalizedDetail.card_merchant ?? ''),
        card_brand: String(normalizedDetail.card_brand ?? ''),
        card_no: String(normalizedDetail.card_no ?? ''),
        spoke_resident: normalizeMulti(normalizedDetail.spoke_resident),
        certificate_on_file: String(normalizedDetail.certificate_on_file ?? ''),
        confirmed_move_in: String(normalizedDetail.confirmed_move_in ?? ''),
      }

      const applicationRow =
        appInfo.co_resident_application?.[applicantId] ??
        appInfo.co_resident_application?.[String(applicantId)] ??
        null

      setDetail(normalizedDetail)
      setApplicationInfo(appInfo)
      setApplicationData(applicationRow)
      setNotes(noteRows)
      setInvestigation(applyPaymentDefaults(baseInvestigation, appInfo, normalizedDetail))
      setDecision({
        chargeback_type: String(normalizedDetail.chargeback_type ?? ''),
        lost_reason: String(normalizedDetail.lost_reason ?? ''),
        deposited_liberty: String(normalizedDetail.deposited_liberty ?? ''),
        payment_status_updated: String(normalizedDetail.payment_status_updated ?? ''),
        merchant_decision_date: toYmd(normalizedDetail.merchant_decision_date),
        funds_date: toYmd(normalizedDetail.funds_date),
        expire_application: normalizeBoolean(normalizedDetail.expire_application),
        cancelled_application: normalizeBoolean(normalizedDetail.cancelled_application),
        re_invoice: normalizeBoolean(normalizedDetail.re_invoice),
        file_fraud: normalizeBoolean(normalizedDetail.file_fraud),
        refunded: normalizeBoolean(normalizedDetail.refunded),
      })
    } catch (err) {
      showApiError(err)
    } finally {
      setLoading(false)
    }
  }, [applicantId, applyPaymentDefaults, logId])

  useEffect(() => {
    void loadPage()
  }, [loadPage])

  const patchInvestigation = (patch: Partial<ChargebackDetailInvestigationForm>) =>
    setInvestigation((prev) => ({ ...prev, ...patch }))

  const patchDecision = (patch: Partial<ChargebackDetailDecisionForm>) =>
    setDecision((prev) => ({ ...prev, ...patch }))

  const validateInvestigation = () => {
    const required: Array<[keyof ChargebackDetailInvestigationForm, string]> = [
      ['chargeback_status', 'Chargeback Status'],
      ['adjustment_reason', 'Chargeback Reason'],
      ['case_number', 'Case Number'],
      ['transaction_id', 'Transaction #'],
      ['chargeback_amount', 'Chargeback Amount'],
      ['response_deadline', 'Response Deadline'],
      ['transaction_date', 'Original Transaction Date'],
      ['chargeback_notification_date', 'Received Date'],
      ['card_merchant', 'Card Merchant'],
      ['certificate_on_file', 'Certificate on File'],
      ['confirmed_move_in', 'Confirmed Move-In'],
    ]
    const missing = required
      .filter(([key]) => !String(investigation[key] ?? '').trim())
      .map(([, label]) => label)
    if (!investigation.spoke_resident.length) missing.push('Spoke with resident')
    if (investigation.card_merchant === 'CARDCONNECT') {
      if (!investigation.card_brand) missing.push('Card Brand')
      if (!investigation.card_no) missing.push('Last 4 digits of Card')
    }
    if (missing.length) {
      showError(`Please complete required fields: ${missing.join(', ')}.`)
      return false
    }
    return true
  }

  const validateDecision = () => {
    if (!isDecisionAllowed(detail)) {
      showError('Decision can be updated only after the chargeback is challenged or complete.')
      return false
    }
    const missing: string[] = []
    if (!decision.chargeback_type) missing.push('Result')
    if (!decision.merchant_decision_date) missing.push('Merchant Decision Date')
    if (decision.chargeback_type !== 'WON_CREDIT' && !decision.lost_reason) {
      missing.push('Lost Reason')
    }
    if (decision.chargeback_type === 'WON_CREDIT' && !decision.deposited_liberty) {
      missing.push('Deposited To Liberty Rent')
    }
    if (missing.length) {
      showError(`Please complete required fields: ${missing.join(', ')}.`)
      return false
    }
    return true
  }

  const saveActiveTab = async () => {
    if (!detail?.pa_id) return
    if (activeTab === 'investigation' && !validateInvestigation()) return
    if (activeTab === 'decision' && !validateDecision()) return

    const payload =
      activeTab === 'investigation'
        ? {
            ...investigation,
            spoke_resident: investigation.spoke_resident,
            chargeback_amount: amountForApi(investigation.chargeback_amount),
          }
        : { ...decision }

    setSaving(true)
    try {
      await updateChargebackPaymentAdjustment(detail.pa_id, payload)
      showSuccess('Submitted Successfully!')
      await loadPage()
      await loadNotes()
    } catch (err) {
      showApiError(err)
    } finally {
      setSaving(false)
    }
  }

  const addNote = async () => {
    const note = noteDraft.trim()
    if (!note || !logId) return
    setSaving(true)
    try {
      const status = investigation.chargeback_status
      await addChargebackNote(logId, status ? `${status} - ${note}` : note)
      setNoteDraft('')
      showSuccess('Note Added Successfully!')
      await loadNotes()
      await loadPage()
    } catch (err) {
      showApiError(err)
    } finally {
      setSaving(false)
    }
  }

  const handleGeneratePacket = async () => {
    if (!applicantId) return
    setPacketConfirmOpen(false)
    setSaving(true)
    try {
      await generateChargebackPacket(applicantId)
      showSuccess('Chargeback Packet Generated Successfully!')
      await loadPage()
    } catch (err) {
      showApiError(err)
    } finally {
      setSaving(false)
    }
  }

  const handleDownloadPacket = () => {
    if (!applicantId) return
    try {
      openChargebackPacketDownload(applicantId)
    } catch (err) {
      showApiError(err)
    }
  }

  const handleUpload = async (
    fieldName: 'investigation_file' | 'decision_file',
    files: ChargebackDetailUploadSelection[],
  ) => {
    if (!detail?.pa_id || !files.length) return
    setFileBusy(true)
    try {
      await uploadChargebackFiles(detail.pa_id, fieldName, files)
      showSuccess('File Added Successfully')
      await refreshChargebackFiles()
    } catch (err) {
      showApiError(err)
    } finally {
      setFileBusy(false)
    }
  }

  const confirmDeleteFile = async () => {
    const attachmentId = deleteFileId
    if (attachmentId == null) return
    // Dismiss first (Angular closes the dialog on click); the reload runs behind it.
    setDeleteFileId(null)
    setFileBusy(true)
    try {
      await deleteChargebackFile(attachmentId)
      showSuccess('File Removed Successfully!')
      await refreshChargebackFiles()
    } catch (err) {
      showApiError(err)
    } finally {
      setFileBusy(false)
    }
  }

  return {
    activeTab,
    setActiveTab,
    loading,
    saving,
    fileBusy,
    detail,
    applicationInfo,
    applicationData,
    notes,
    noteDraft,
    setNoteDraft,
    investigation,
    decision,
    packetConfirmOpen,
    setPacketConfirmOpen,
    deleteFileId,
    setDeleteFileId,
    tabs,
    patchInvestigation,
    patchDecision,
    saveActiveTab,
    addNote,
    handleGeneratePacket,
    handleDownloadPacket,
    handleUpload,
    confirmDeleteFile,
  }
}
